import { useEffect, useRef, useState } from "react";
import { Reorder, useDragControls } from "framer-motion";
import { ArrowDown, ArrowUp, Check, Download, FileSpreadsheet, GripVertical, Trash2, Upload, X } from "lucide-react";
import { API, api, mediaUrl } from "@/lib/api";

const postFiles = async (path, field, files) => {
  const fd = new FormData();
  Array.from(files).forEach(f => fd.append(field, f));
  const res = await fetch(`${API}${path}`, { method: "POST", body: fd, credentials: "include" });
  const data = await res.json();
  if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Request failed");
  return data;
};

export const ImportMembersModal = ({ onClose, onDone }) => {
  const ref = useRef();
  const [sendLinks, setSendLinks] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [fileName, setFileName] = useState("");

  const run = async e => {
    e.preventDefault();
    const f = ref.current?.files?.[0];
    if (!f) return setErr("Choose a CSV or Excel file first.");
    setBusy(true); setErr("");
    try { setResult(await postFiles(`/admin/members/import?send_links=${sendLinks}`, "file", [f])); onDone(); }
    catch (ex) { setErr(ex.message); }
    finally { setBusy(false); }
  };

  const template = async () => {
    const res = await fetch(`${API}/admin/members/import-template`, { credentials: "include" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(await res.blob()); a.download = "dcea-members-template.csv"; a.click(); URL.revokeObjectURL(a.href);
  };

  return (
    <div className="admin-modal" role="dialog" data-testid="import-members-modal">
      <form className="admin-modal-body" onSubmit={run}>
        <div className="admin-modal-head">
          <div><span className="eyebrow">Bulk import</span><h2>Import members</h2></div>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="import-close"><X size={18} /></button>
        </div>
        <div className="hint">Columns recognised: <b>Name, Email</b> (required), Phone, Organization, City, Qualification, Member ID, Membership Type. Existing emails are updated, new ones get an approved member account.</div>
        <div className="admin-modal-fields">
          <label className="media-field">CSV or Excel file
            <div className="media-field-row">
              <input value={fileName} readOnly placeholder="No file chosen" data-testid="import-file-name" />
              <button type="button" className="button button-ghost dark" onClick={() => ref.current?.click()} data-testid="import-choose-file"><FileSpreadsheet size={14} /> Choose file</button>
              <input ref={ref} type="file" hidden accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={e => setFileName(e.target.files?.[0]?.name || "")} data-testid="import-file-input" />
            </div>
          </label>
          <label className="media-field check"><input type="checkbox" checked={sendLinks} onChange={e => setSendLinks(e.target.checked)} data-testid="import-send-links" /> Email each new member a secure password-setup link</label>
        </div>
        {err && <div className="error-message" data-testid="import-error">{err}</div>}
        {result && (
          <div className="success-message" data-testid="import-result">
            <b>Import complete.</b> {result.created} created · {result.updated} updated · {result.skipped} skipped · {result.links_sent} setup links sent.
            {result.errors?.length > 0 && <ul style={{ margin: "10px 0 0 18px", fontSize: 12 }}>{result.errors.slice(0, 8).map((e, i) => <li key={i}>{e}</li>)}</ul>}
          </div>
        )}
        <div className="admin-modal-actions">
          <button type="button" className="button button-ghost dark" onClick={template} data-testid="import-template-button"><Download size={14} /> Template</button>
          <button className="button button-gold" disabled={busy} data-testid="import-submit-button"><Upload size={14} /> {busy ? "Importing…" : "Import"}</button>
        </div>
      </form>
    </div>
  );
};

const PhotoRow = ({ p, i, total, onCaption, onMove, onRemove }) => {
  const controls = useDragControls();
  return (
    <Reorder.Item value={p} dragListener={false} dragControls={controls} className="photo-row" data-testid={`album-photo-${i}`}>
      <span className="layout-grip" onPointerDown={e => controls.start(e)} data-testid={`album-photo-grip-${i}`}><GripVertical size={16} /></span>
      <img src={mediaUrl(p.url)} alt="" />
      <input value={p.caption || ""} onChange={e => onCaption(p.id, e.target.value)} placeholder="Add a caption (optional)" maxLength={200} data-testid={`album-photo-caption-${i}`} />
      <div className="layout-actions">
        <button type="button" disabled={i === 0} onClick={() => onMove(i, -1)} aria-label="Move up" data-testid={`album-photo-up-${i}`}><ArrowUp size={14} /></button>
        <button type="button" disabled={i === total - 1} onClick={() => onMove(i, 1)} aria-label="Move down" data-testid={`album-photo-down-${i}`}><ArrowDown size={14} /></button>
        <button type="button" className="danger" onClick={() => onRemove(p)} aria-label="Remove" data-testid={`album-photo-delete-${i}`}><Trash2 size={14} /></button>
      </div>
    </Reorder.Item>
  );
};

export const AlbumPhotosModal = ({ album, onClose, onChanged }) => {
  const ref = useRef();
  const [photos, setPhotos] = useState(album.photos || []);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  useEffect(() => { setPhotos(album.photos || []); setDirty(false); }, [album]);

  const edit = next => { setPhotos(next); setDirty(true); setMsg(""); };
  const caption = (id, v) => edit(photos.map(p => (p.id === id ? { ...p, caption: v } : p)));
  const move = (i, d) => { const n = [...photos]; const [it] = n.splice(i, 1); n.splice(i + d, 0, it); edit(n); };

  const upload = async e => {
    const files = e.target.files;
    if (!files?.length) return;
    setBusy(true); setErr("");
    try { const d = await postFiles(`/admin/albums/${album.id}/photos`, "files", files); setPhotos(p => [...p, ...d.photos]); onChanged(); }
    catch (ex) { setErr(ex.message); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const remove = async p => {
    try { await api(`/admin/albums/${album.id}/photos/${p.id}`, { method: "DELETE" }); setPhotos(ps => ps.filter(x => x.id !== p.id)); onChanged(); }
    catch (ex) { setErr(ex.message); }
  };
  const save = async () => {
    setBusy(true); setErr("");
    try { const d = await api(`/admin/albums/${album.id}/photos`, { method: "PUT", body: JSON.stringify({ photos: photos.map(p => ({ id: p.id, caption: p.caption || "" })) }) }); setPhotos(d.photos); setDirty(false); setMsg("Order and captions saved."); onChanged(); }
    catch (ex) { setErr(ex.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="admin-modal" role="dialog" data-testid="album-photos-modal">
      <div className="admin-modal-body" style={{ width: "min(900px,100%)" }}>
        <div className="admin-modal-head">
          <div><span className="eyebrow">Album photos · {photos.length}</span><h2 style={{ textTransform: "none" }}>{album.title}</h2></div>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="album-photos-close"><X size={18} /></button>
        </div>
        <div className="dropzone" onClick={() => ref.current?.click()} data-testid="album-dropzone">
          <Upload size={26} /><b>{busy ? "Working…" : "Add photos from your device"}</b><span>Select multiple JPG, PNG or WEBP files · up to 10 MB each · drag the handle to reorder</span>
          <input ref={ref} type="file" hidden multiple accept="image/*" onChange={upload} data-testid="album-file-input" />
        </div>
        {err && <div className="error-message" data-testid="album-error">{err}</div>}
        {msg && <div className="success-message" data-testid="album-saved">{msg}</div>}
        <Reorder.Group axis="y" values={photos} onReorder={edit} className="layout-list" as="div" data-testid="album-photo-grid">
          {photos.map((p, i) => <PhotoRow key={p.id} p={p} i={i} total={photos.length} onCaption={caption} onMove={move} onRemove={remove} />)}
          {!photos.length && <div className="empty-state">No photos yet.</div>}
        </Reorder.Group>
        <div className="admin-modal-actions">
          <button type="button" className="button button-ghost dark" onClick={onClose} data-testid="album-photos-done">Close</button>
          <button type="button" className="button button-gold" onClick={save} disabled={!dirty || busy} data-testid="album-photos-save"><Check size={15} /> Save order & captions</button>
        </div>
      </div>
    </div>
  );
};
